(() => {
  const pending = new WeakMap();

  function pictureFor(slide) {
    return slide?.querySelector('picture');
  }

  function ready(slide) {
    const picture = pictureFor(slide);
    const image = picture?.querySelector('img');
    return !picture || (image?.complete && image.naturalWidth > 0);
  }

  function hydrate(slide) {
    const picture = pictureFor(slide);
    const image = picture?.querySelector('img');
    if (!picture || !image) return Promise.resolve();
    if (ready(slide)) return image.decode?.().catch(() => {}) || Promise.resolve();
    if (pending.has(picture)) return pending.get(picture);

    const promise = new Promise((resolve, reject) => {
      const cleanup = () => {
        image.removeEventListener('load', onLoad);
        image.removeEventListener('error', onError);
        pending.delete(picture);
      };
      const onLoad = () => {
        cleanup();
        Promise.resolve(image.decode?.()).catch(() => {}).then(resolve);
      };
      const onError = () => {
        cleanup();
        reject(new Error('Slide image unavailable'));
      };
      image.addEventListener('load', onLoad, { once: true });
      image.addEventListener('error', onError, { once: true });
      if (picture.hasAttribute('data-cf-slide-deferred')) {
        picture.querySelectorAll('source').forEach((source) => {
          if (source.dataset.sizes) source.sizes = source.dataset.sizes;
          if (source.dataset.srcset) source.srcset = source.dataset.srcset;
        });
        image.loading = 'eager';
        if (image.dataset.sizes) image.sizes = image.dataset.sizes;
        if (image.dataset.srcset) image.srcset = image.dataset.srcset;
        if (image.dataset.src) image.src = image.dataset.src;
        picture.removeAttribute('data-cf-slide-deferred');
      }
      if (image.complete && image.naturalWidth > 0) queueMicrotask(onLoad);
    });
    pending.set(picture, promise);
    return promise;
  }

  function attach(slider) {
    if (slider.cfImageDeliveryAttached) return;
    slider.cfImageDeliveryAttached = true;
    const slideTo = slider.slideTo.bind(slider);
    let request = 0;

    slider.slideTo = (index, ...args) => {
      const target = slider.slides[Number(index)];
      const sequence = ++request;
      if (ready(target)) return slideTo(index, ...args);
      hydrate(target).then(() => {
        if (!slider.destroyed && sequence === request) slideTo(index, ...args);
      }).catch(() => {});
      return false;
    };

    const preloadNext = () => {
      if (slider.destroyed) return;
      const next = slider.slides[(slider.activeIndex + 1) % slider.slides.length];
      hydrate(next).catch(() => {});
    };
    slider.on('slideChange', preloadNext);
    hydrate(slider.slides[slider.activeIndex]).then(() => {
      requestAnimationFrame(() => requestAnimationFrame(preloadNext));
    }).catch(() => {});
  }

  window.ComfortFlexSlideImages = { attach };
})();
